'use client';
import { FC, useCallback, useMemo, useState } from 'react';
import { Box, Typography, Divider, Select, MenuItem } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { FormTextField, FormAutocomplete, FormDatePicker } from '@/components/common/FormComponents';
import { StandardDialog } from '@/components/common/StandardDialog';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { OptionKey } from '@/lib/options/optionTypes';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { useAuthStore } from '@/stores/authStore';
import { useBackendStore } from '@/stores/backendStore';
import {
  SampleActivityDto,
  SampleDto,
  CharacterizationInstrumentDto,
  CharacterizationTechniqueDto,
  StatusDto,
  ActivityCategoryDto,
  VisibilityDto,
} from '@/api/ApiClient';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { useSnackbar } from '@/hooks/useSnackbar';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { getVisibilityTypeDisplay, VISIBILITY_OPTIONS } from '@/lib/types/visibilityTypes';
import { getCharacterizationInstrumentsForFacility } from '@/lib/utils/characterizationInstruments';
import { getCharacterizationFacilityChoicesForSample } from '@/lib/utils/sampleActivityLifecycleEligibility';
import { validateRequiredToSave } from '@/lib/utils/saveValidation';
import { createCharacterizationActivitySaveValidation } from '@/lib/validation/saveValidationBuilders';

const OPTION_KEYS: OptionKey[] = [
  'researcherOptions',
  'characterizationInstruments',
  'characterizationTechniques',
  'characterizationDropdownChoices',
  'visibility',
];

interface AddCharacterizationProps {
  open: boolean;
  onClose: () => void;
  sample: SampleDto;
  parentActivity?: SampleActivityDto;
  onSubmit?: (activity: SampleActivityDto) => void;
}

// Facility-specific form components
interface InternalFacilityFormProps {
  selectedFacility: string;
  selectedInstrument: CharacterizationInstrumentDto | null;
  instrumentOptions: CharacterizationInstrumentDto[];
  onInstrumentChange: (instrument: CharacterizationInstrumentDto | null) => void;
}

const InternalFacilityForm: FC<InternalFacilityFormProps> = ({
  selectedInstrument,
  instrumentOptions,
  onInstrumentChange,
}) => (
  <FormAutocomplete
    options={instrumentOptions}
    getOptionLabel={(option) => `${option.id} - ${option.name}` || ''}
    value={selectedInstrument}
    onChange={(_, newValue) => onInstrumentChange(newValue)}
    label="Instrument"
    required
    isOptionEqualToValue={(opt, val) => opt.id === val.id}
    sx={{ flex: 1 }}
  />
);

interface ExternalFacilityFormProps {
  selectedInstrumentType: CharacterizationTechniqueDto | null;
  instrumentName: string;
  instrumentDoi: string;
  instrumentOptions: CharacterizationTechniqueDto[];
  onInstrumentTypeChange: (type: CharacterizationTechniqueDto | null) => void;
  onInstrumentNameChange: (name: string) => void;
  onInstrumentDoiChange: (doi: string) => void;
}

const ExternalFacilityForm: FC<ExternalFacilityFormProps> = ({
  selectedInstrumentType,
  instrumentName,
  instrumentDoi,
  instrumentOptions,
  onInstrumentTypeChange,
  onInstrumentNameChange,
  onInstrumentDoiChange,
}) => (
  <>
    <FormAutocomplete
      options={instrumentOptions}
      getOptionLabel={(option) => option.name || ''}
      value={selectedInstrumentType}
      onChange={(_, newValue) => onInstrumentTypeChange(newValue)}
      label="Characterization Technique"
      required
      isOptionEqualToValue={(opt, val) => opt.id === val.id}
      sx={{ flex: 1 }}
    />
    <Box display="flex" gap={2}>
      <FormTextField
        label="Instrument Name"
        fullWidth
        value={instrumentName}
        onChange={(e) => onInstrumentNameChange(e.target.value)}
        size="small"
        sx={{ flex: 1 }}
        required
      />
      <FormTextField
        label="Instrument DOI"
        fullWidth
        value={instrumentDoi}
        onChange={(e) => onInstrumentDoiChange(e.target.value)}
        size="small"
        sx={{ flex: 1 }}
      />
    </Box>
  </>
);

interface AddCharacterizationDialogInnerProps extends AddCharacterizationProps {
  researcherOptions: { key: string; value: string }[];
  characterizationInstruments: CharacterizationInstrumentDto[];
  characterizationTechniques: CharacterizationTechniqueDto[];
  facilityChoices?: string[];
  initialResearcher: { key: string; value: string } | null;
  initialFacility: string | null;
}

const AddCharacterizationDialogInner: FC<AddCharacterizationDialogInnerProps> = ({
  open,
  onClose,
  sample,
  parentActivity,
  onSubmit,
  researcherOptions,
  characterizationInstruments,
  characterizationTechniques,
  facilityChoices,
  initialResearcher,
  initialFacility,
}) => {
  const backend = useBackendStore.getState().getBackend();
  const { showSnackbar } = useSnackbar();

  // Form state
  const [selectedInstrument, setSelectedInstrument] = useState<CharacterizationInstrumentDto | null>(null);
  const [selectedInstrumentType, setSelectedInstrumentType] = useState<CharacterizationTechniqueDto | null>(null);
  const [selectedFacility, setSelectedFacility] = useState<string | null>(initialFacility);
  const [selectedResearcher, setSelectedResearcher] = useState<{ key: string; value: string } | null>(
    initialResearcher
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedVisibility, setSelectedVisibility] = useState<VisibilityDto>(VisibilityDto.UserPI);
  const [description, setDescription] = useState('');
  const [instrumentName, setInstrumentName] = useState<string>('');
  const [instrumentDoi, setInstrumentDoi] = useState<string>('');

  // Track user interaction - once user does anything, always show warning on close
  const { hasUnsavedChanges, markAsInteracted, reset } = useUserInteraction();

  const shippedRequiresExternal = sample.shipped === true;
  const facilityOptions = useMemo(
    () => getCharacterizationFacilityChoicesForSample(sample, facilityChoices),
    [sample, facilityChoices]
  );

  // Computed values
  const isExternalFacility = selectedFacility === 'External';

  // Reset all states when modal is closed
  const resetAllStates = () => {
    setSelectedDate(new Date());
    setSelectedFacility(initialFacility);
    setSelectedInstrument(null);
    setSelectedInstrumentType(null);
    setSelectedResearcher(initialResearcher);
    setSelectedVisibility(VisibilityDto.UserPI);
    setDescription('');
    setInstrumentName('');
    setInstrumentDoi('');
    reset(); // Reset user interaction state
    onClose();
  };

  const handleFacilityChange = (newFacility: string | null) => {
    setSelectedFacility(newFacility);
    setSelectedInstrument(null);
    setSelectedInstrumentType(null);
    markAsInteracted();
  };

  const handleInstrumentChange = (instrument: CharacterizationInstrumentDto | null) => {
    setSelectedInstrument(instrument);
    markAsInteracted();
  };

  const handleInstrumentTypeChange = (type: CharacterizationTechniqueDto | null) => {
    setSelectedInstrumentType(type);
    markAsInteracted();
  };

  const handleInstrumentNameChange = (name: string) => {
    setInstrumentName(name);
    markAsInteracted();
  };

  const handleInstrumentDoiChange = (doi: string) => {
    setInstrumentDoi(doi);
    markAsInteracted();
  };

  // Validation logic
  const isValid = useCallback(() => {
    const saveValidation = validateRequiredToSave(
      createCharacterizationActivitySaveValidation({
        userId: selectedResearcher?.key,
        date: selectedDate ?? undefined,
        facility: selectedFacility ?? undefined,
        visibility: selectedVisibility,
        instrument: selectedInstrument?.id,
        characterizationTechnique: selectedInstrumentType?.id,
        desc: instrumentName,
      })
    );
    return saveValidation.isValid;
  }, [
    selectedResearcher,
    selectedDate,
    selectedFacility,
    selectedVisibility,
    selectedInstrumentType,
    instrumentName,
    selectedInstrument,
  ]);

  // Assemble the DTO and submit to backend
  const handleSubmit = async () => {
    log.debug('[AddCharacterizationDialog] Submitting characterization activity');

    if (!isValid()) {
      log.error('[AddCharacterizationDialog] Missing required fields');
      showSnackbar('Please fill in all required fields.', 'error');
      return;
    }

    // sample.id should normally be set here, even for a in-situ activity?
    // But let's be safe and use the parent activity's sample id if available.
    const effectiveSampleId = parentActivity?.sampleId ?? sample.id;
    if (!effectiveSampleId) {
      showSnackbar('Sample ID is not available.', 'error');
      return;
    }

    const newActivity: SampleActivityDto = {
      sampleId: effectiveSampleId,
      parentActivityId: parentActivity?.id,
      userId: selectedResearcher!.key,
      date: selectedDate!,
      instrument: isExternalFacility ? undefined : selectedInstrument?.id,
      facility: selectedFacility!,
      characterizationTechnique: isExternalFacility
        ? selectedInstrumentType?.id
        : selectedInstrument?.characterizationTechnique,
      instrumentDOI: isExternalFacility ? instrumentDoi || undefined : undefined,
      desc: description || '',
      category: ActivityCategoryDto.Characterization,
      status: StatusDto.Pending,
      visibility: VisibilityDto.UserPI,
    };

    try {
      const created = await backend.sampleActivity.create(newActivity);
      log.debug('[AddCharacterizationDialog] Characterization activity created:', created);

      onSubmit?.(created);
      onClose();
      showSnackbar('Characterization activity created successfully', 'success');
    } catch (error) {
      log.error('[AddCharacterizationDialog] Error creating characterization activity:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to create characterization activity.', 'error');
    }
    resetAllStates();
  };

  return (
    <StandardDialog
      open={open}
      onClose={resetAllStates}
      title="Start Characterization Activity"
      maxWidth="md"
      onSubmit={handleSubmit}
      submitText="Start Characterization"
      submitDisabled={!isValid()}
      hasUnsavedChanges={hasUnsavedChanges}
      unsavedChangesMessage="You have unsaved changes to this characterization activity. Are you sure you want to discard them?"
    >
      {/* Researcher and Date */}
      <Box display="flex" gap={2}>
        <FormAutocomplete
          fullWidth
          options={researcherOptions}
          getOptionLabel={(option) => option.value}
          value={selectedResearcher}
          onChange={(_, newValue) => {
            setSelectedResearcher(newValue);
            markAsInteracted();
          }}
          label="Researcher"
          required
        />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <FormDatePicker
            label="Date"
            value={selectedDate}
            onChange={(newValue) => {
              setSelectedDate(newValue);
              markAsInteracted();
            }}
            required
          />
        </LocalizationProvider>
      </Box>

      <Divider />

      {/* Facility Selection */}
      <Box display="flex" gap={2}>
        <FormAutocomplete
          options={facilityOptions}
          value={selectedFacility}
          onChange={(_, newValue) => handleFacilityChange(newValue)}
          label="Facility"
          required
          isOptionEqualToValue={(opt, val) => opt === val}
          sx={{ flex: 1 }}
          disabled={shippedRequiresExternal}
        />

        {/* Instrument/Technique Selection */}
        {selectedFacility ? (
          isExternalFacility ? (
            <ExternalFacilityForm
              selectedInstrumentType={selectedInstrumentType}
              instrumentName={instrumentName}
              instrumentDoi={instrumentDoi}
              instrumentOptions={characterizationTechniques}
              onInstrumentTypeChange={handleInstrumentTypeChange}
              onInstrumentNameChange={handleInstrumentNameChange}
              onInstrumentDoiChange={handleInstrumentDoiChange}
            />
          ) : (
            <InternalFacilityForm
              selectedFacility={selectedFacility}
              selectedInstrument={selectedInstrument}
              instrumentOptions={getCharacterizationInstrumentsForFacility(
                characterizationInstruments,
                selectedFacility,
                selectedInstrument?.id
              )}
              onInstrumentChange={handleInstrumentChange}
            />
          )
        ) : null}
      </Box>

      <Divider />

      {/* Visibility */}
      <Box>
        <Typography variant="subtitle2" color="text.secondary">
          Visibility
        </Typography>
        <Select
          value={selectedVisibility}
          onChange={(e) => {
            setSelectedVisibility(e.target.value as VisibilityDto);
            markAsInteracted();
          }}
          label="Visibility"
          fullWidth
          size="small"
        >
          {VISIBILITY_OPTIONS.map((value) => (
            <MenuItem key={value} value={value}>
              {getVisibilityTypeDisplay(value)}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Divider />

      {/* Notes */}
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Notes
        </Typography>
        <RichTextEditor
          value={description}
          onChange={(value) => {
            setDescription(value);
            markAsInteracted();
          }}
        />
      </Box>
    </StandardDialog>
  );
};

const AddCharacterizationDialog: FC<AddCharacterizationProps> = ({
  open,
  onClose,
  sample,
  parentActivity,
  onSubmit,
}) => {
  const { options, loading: optionsLoading } = useSelectOptions(OPTION_KEYS);
  const { currentUser } = useAuthStore();

  const researcherOptions = useMemo(() => options.researcherOptions ?? [], [options.researcherOptions]);
  const characterizationInstruments = useMemo(
    () => options.characterizationInstruments ?? [],
    [options.characterizationInstruments]
  );
  const characterizationTechniques = useMemo(
    () => options.characterizationTechniques ?? [],
    [options.characterizationTechniques]
  );
  const facilityChoices = options.characterizationDropdownChoices?.facilities;

  const initialResearcher = useMemo(() => {
    if (!currentUser?.id) return null;
    return researcherOptions.find((r) => r.key === currentUser.id) ?? null;
  }, [researcherOptions, currentUser]);

  const initialFacility = useMemo(() => {
    if (sample?.shipped === true) return 'External';
    if (!parentActivity) return null;
    const inSituOption = (facilityChoices ?? []).find((option) => option.toLowerCase() === 'in-situ');
    return inSituOption ?? null;
  }, [sample?.shipped, parentActivity, facilityChoices]);

  const dialogInitKey = useMemo(
    () =>
      `${sample?.id ?? 'sample'}|${parentActivity?.id ?? 'root'}|${currentUser?.id ?? 'anon'}|${
        currentUser?.selectedRole ?? ''
      }|${String(currentUser?.usingSignInAs ?? false)}`,
    [sample?.id, parentActivity?.id, currentUser?.id, currentUser?.selectedRole, currentUser?.usingSignInAs]
  );

  const waitingMessage = !sample ? 'No sample selected.' : optionsLoading ? 'Loading options...' : null;
  if (waitingMessage) {
    return (
      <StandardDialog
        open={open}
        onClose={onClose}
        title="Start Characterization Activity"
        maxWidth="md"
        onSubmit={() => {}}
        submitText="Start Characterization"
        submitDisabled
      >
        <Box sx={{ py: 2 }}>{waitingMessage}</Box>
      </StandardDialog>
    );
  }

  return (
    <AddCharacterizationDialogInner
      key={dialogInitKey}
      open={open}
      onClose={onClose}
      sample={sample}
      parentActivity={parentActivity}
      onSubmit={onSubmit}
      researcherOptions={researcherOptions}
      characterizationInstruments={characterizationInstruments}
      characterizationTechniques={characterizationTechniques}
      facilityChoices={facilityChoices}
      initialResearcher={initialResearcher}
      initialFacility={initialFacility}
    />
  );
};

export { AddCharacterizationDialog };
